import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createSlug,
  fetcher,
  formatLocation,
} from "../../Utils/HelperFunctions";
import useRecordStoreVisit from "../../Hooks/useRecordStoreVisit";
import Header from "../../Components/Header";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../Services/EndPoints";
import defaultProductImage from "../../assets/Svg/default.svg";

import { get } from "../../Services/api.service";
import { LanguageSwitcher } from "../../Components/LanguageSwitcher";
import OptimizedImage from "../../Components/OptimizedImage";
import PinboardShelf from "../../Components/PinboardShelf";
import useSWR from "swr";
import { useNotification } from "../../Components/CustomStatus/UseNotification";
import { CartQuantity } from "../../Components/CartQuantity";
import { FullPageShimmer } from "../../Components/MyStorePageShimmer";

import TrustStrip from "../../Components/Home/TrustStrip";
import ShippingBar from "../../Components/Home/ShippingBar";
import AuthenticityPromiseBar from "../../Components/Home/AuthenticityPromiseBar";
import OwnerBand from "../../Components/Home/OwnerBand";
import CouponLandingBanner from "../../Components/Home/CouponLandingBanner";
import HomeSearchBar from "../../Components/Home/HomeSearchBar";
import CraftClusterRow from "../../Components/Home/CraftClusterRow";
import NewlyArrivedRow from "../../Components/Home/NewlyArrivedRow";
import SareesUnderBanner from "../../Components/Home/SareesUnderBanner";
import PhilosophyBand from "../../Components/Home/PhilosophyBand";
import SupportSmallBiz from "../../Components/Home/SupportSmallBiz";
import KnowYourCraftRow from "../../Components/Home/KnowYourCraftRow";
import BehindScenesRow from "../../Components/Home/BehindScenesRow";
import StoreFooter from "../../Components/StoreFooter";
import WhatsAppFAB from "../../Components/WhatsAppFAB";

export default function MyStore() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storeDetails = useSelector((state) => state.storeDetails);
  const { openToast } = useNotification();

  const lang = localStorage.getItem("i18nextLng");

  // Record one storefront landing visit per session (store home is a landing).
  useRecordStoreVisit();

  const homeEndpoint = Paths?.get_home_page
    ? `${Paths?.get_home_page}?language=${lang}`
    : null;

  const { data: home = {}, isLoading } = useSWR(homeEndpoint, fetcher, {
    staleTime: 5 * 60 * 1000,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 30000,
    errorRetryCount: 2,
    keepPreviousData: true,
    revalidateIfStale: true,
    onError: (err) => {
      openToast({
        content: err?.response?.data?.message || t("myStore.fetchFailed"),
        type: "error",
      });
    },
  });

  const categoryGroups = home?.healthConcerns || [];

  // Store identity for footer / owner band / WhatsApp (same source as Journal & About).
  // Must use `fetcher` (returns res.data) — the SAME fetcher every other about_me
  // consumer uses, so the shared SWR cache holds one consistent shape. Using a
  // different fetcher here poisoned the cache for pages navigated to afterward.
  const { data: about } = useSWR(Paths?.about_me, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    dedupingInterval: 5 * 60 * 1000,
  });
  const ownerFirstName = about?.firstName || storeDetails?.userName || "Meera";
  const ownerFullName =
    [about?.firstName, about?.lastName].filter(Boolean).join(" ") || undefined;
  const brandName =
    about?.storeName || storeDetails?.storeName || "The Crafts Archive";
  const storeLocation = formatLocation(about?.city, about?.state) || undefined;
  const onWhatsAppClick = () =>
    window.open(
      about?.mobileNumber
        ? `https://wa.me/91${about.mobileNumber}`
        : "https://wa.me/",
      "_blank",
    );

  const userData = JSON.parse(localStorage.getItem("userData"));
  const token = userData?.token;
  const isLoggedin = !token ? false : true;

  // Category grid: "add-on" categories (Beauty, Wellness) are returned capped by
  // get_home_page and get a "add-on" tag + a "See all" (+) tile. Clicking it
  // fetches the FULL health-concern list for that category from
  // get_health_concerns_by_category?categoryId={id} and shows every tile.
  // Add-on / capped secondary verticals: they arrive with only a few tiles from
  // get_home_page (full list fetched via get_health_concerns_by_category behind a
  // "See all"(+) tile) and carry an "add-on" pill. "Others" is one too (3 of 47).
  // Fashion categories (Women's/Jewelry) share categoryId 5 so See-all can't
  // disambiguate them — deliberately excluded.
  const isAddOnCategory = (item) =>
    ["beauty", "wellness", "others"].includes(
      (item?.englishName || item?.categoryName || "").toLowerCase(),
    );
  const [expandedCats, setExpandedCats] = useState({}); // categoryName -> full healthConcerns[]
  const [expandingKey, setExpandingKey] = useState(null);

  const seeAllCategory = (item, key) => {
    if (expandedCats[key] || expandingKey) return;
    setExpandingKey(key);
    get(
      `${Paths?.get_health_concerns_by_category}?categoryId=${item?.categoryId}&language=${lang}`,
    )
      .then((env) => {
        const list = env?.data?.[0]?.healthConcerns || [];
        setExpandedCats((prev) => ({ ...prev, [key]: list }));
      })
      .catch(() =>
        openToast({ content: t("myStore.loadItemsFailed"), type: "error" }),
      )
      .finally(() => setExpandingKey(null));
  };

  const openSub = (item, sub) => {
    const clickedItem = createSlug(item?.englishName);
    if (clickedItem === "womens-fashion" || clickedItem === "mens-fashion") {
      sessionStorage.setItem("clickedHealthConcern", clickedItem);
    } else sessionStorage.removeItem("clickedHealthConcern");
    navigate(
      `/shop/${createSlug(item?.englishName)}/${createSlug(sub?.englishName)}/${sub?.id}`,
    );
  };

  if (isLoading) return <FullPageShimmer />;

  return (
    <div className="min-h-screen bg-white">
      {/* KEEP: store header (menu · logo · name · cart) */}
      <div className="bg-white px-4">
        <Header
          image={storeDetails?.storeUrl}
          name={storeDetails?.storeName || ""}
          userName={ownerFullName || ownerFirstName}
          location={about?.city}
          showMenu={true}
          onBellClick={() => navigate("/notifications")}
          onShareClick={() => navigate("/partner/share-store")}
          endContent={<CartQuantity isInvisible={false} />}
          isAffiliate={storeDetails?.affiliated}
          isDashboard={false}
          startContent={<LanguageSwitcher isLoggedin={isLoggedin} />}
        />
      </div>

      {/* NEW: trust strip + shipping announcement */}
      <TrustStrip />
      <ShippingBar />

      {/* NEW: the store's money-backed authenticity guarantee. Third and last strip in this run,
          placed here rather than higher because TrustStrip and ShippingBar are unconditional and
          this one is the only strip of the three that opens something. Above ShippingBar it would
          separate the two informational strips that belong together.
          ⚠️ It duplicates — and partly contradicts — PhilosophyBand's "Every piece, independently
          tested" callout further down this page (burn test vs NABL lab; every piece vs sarees;
          print/weave vs fabric/yarn/silk/zari). Flagged for Satish; no copy removed here. */}
      <AuthenticityPromiseBar />

      {/* owner band + search bar — stacked full-width on mobile; on desktop
          they share one row as EQUAL halves (search left, owner right), each
          lg:basis-1/2 so neither dominates. The row keeps the page's px-4 edge
          rhythm so it aligns with the category/craft sections below, with extra
          vertical padding so the row breathes. `contents` keeps the mobile
          layout byte-identical (no wrapper box below lg). */}
      <div className="contents lg:flex lg:items-stretch lg:gap-4 lg:px-4 lg:py-4">
        <OwnerBand ownerName={ownerFirstName} />
        <HomeSearchBar />
      </div>

      {/* NEW: festival banner — the live seller coupon that drives a landing page, as a block
          rather than the 11.5px strip above the header (which stays, unchanged, and still links
          there). Here rather than higher because the three strips under the header are
          unconditional and this is not: above them the page would jump between a store with a
          festival coupon and one without. Renders nothing at all when there is no such coupon —
          no block, no gap. Deliberately NOT inside PromoStack: that is sticky and publishes
          --wb-offer-h, so a banner in it would follow the shopper down the page. */}
      <CouponLandingBanner ownerName={ownerFirstName} />

      {/* NEW: newly arrived sarees — in-stock sarees this store selected in the last 15 days.
          The COUNT inside that window drives the layout (CEO spec, 2026-09-03): 0-1 hides the
          section, 2 renders a row of two, 3 a scroll rail, 4+ a random four. Sits between the
          search/owner row and the pinboard. Renders nothing when the backend omits
          `newArrivals`, so no empty state. */}
      <NewlyArrivedRow data={home?.newArrivals} ownerName={ownerFirstName} />

      {/* NEW: "Sarees under ₹2,000" — opens the Sarees listing with price=-2000. Right after
          Newly arrived so the saree content sits together, and well clear of the festival banner
          above (two big promo blocks back to back would compete). Renders nothing when the store
          has no Sarees category, no in-stock saree at ₹2,000 or less, or a non-INR shopper. */}
      <SareesUnderBanner categoryGroups={categoryGroups} ownerName={ownerFirstName} />

      {/* KEEP: pinboard shelf */}
      <PinboardShelf boards={home?.pinBoards?.boards} />

      {/* KEEP: category grid (grouped subcategory grids per top category) */}
      <section className="px-4 pt-[6px] pb-[22px]">
        {Array.isArray(categoryGroups) &&
          categoryGroups.map((item, index) => {
            // categoryName is unique per group (categoryId collides across some
            // groups), so it keys both React and the expanded-state map.
            const key = item?.categoryName;
            const addOn = isAddOnCategory(item);
            const fullList = expandedCats[key]; // full set once "See all" is tapped
            const isExpanded = !!fullList;
            // Add-on categories arrive capped from get_home_page + a "See all"(+)
            // tile that fetches the full list; primary categories show everything.
            const visibleSubs = isExpanded
              ? fullList
              : item?.healthConcerns || [];
            const showSeeAll = addOn && !isExpanded;
            return (
              <div key={key} className={index > 0 ? "mt-[18px]" : ""}>
                <h2 className="text-[17px] font-[500] text-ink-900 mb-[11px]">
                  {item?.categoryName}
                  {addOn && (
                    <span className="align-middle ml-[6px] text-[10px] font-[500] text-ink-400 bg-wc-gray px-[8px] py-[2px] rounded-pill">
                      {t("myStore.addOn")}
                    </span>
                  )}
                </h2>
                <div className="grid grid-cols-4 gap-x-[8px] gap-y-[13px] md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10">
                  {visibleSubs.map((sub_item) => (
                    <div
                      key={sub_item?.id}
                      className="flex flex-col items-center gap-[6px] cursor-pointer"
                      onClick={() => openSub(item, sub_item)}
                    >
                      <div className="w-full aspect-square rounded-[14px] bg-[#F4F5FF] border border-wc-border grid place-items-center overflow-hidden">
                        <OptimizedImage
                          src={sub_item?.url || defaultProductImage}
                          alt={sub_item?.name}
                          width={96}
                          height={96}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                          priority={index <= 1}
                        />
                      </div>
                      <span className="text-[11px] font-[400] text-ink-900 text-center leading-[1.2] line-clamp-2 underline underline-offset-2 decoration-ink-900">
                        {sub_item?.name}
                      </span>
                    </div>
                  ))}

                  {showSeeAll && (
                    <div
                      className="flex flex-col items-center gap-[6px] cursor-pointer"
                      onClick={() => seeAllCategory(item, key)}
                    >
                      {/* Same tile box as the design's See-all: ➕ emoji @22px */}
                      <div className="w-full aspect-square rounded-[14px] bg-[#F4F5FF] border border-wc-border grid place-items-center overflow-hidden">
                        {expandingKey === key ? (
                          <span className="w-5 h-5 border-2 border-wc-border border-t-s-primary rounded-full animate-spin" />
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            width="22"
                            height="22"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            className="text-ink-900"
                            aria-hidden="true"
                          >
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        )}
                      </div>
                      <span className="text-[11px] font-[400] text-ink-900 text-center leading-[1.2] line-clamp-2 underline underline-offset-2 decoration-ink-900">
                        {t("myStore.seeAll")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </section>

      {/* shop by craft — clothing then jewelry (from get_home_page)
          Each of these get_home_page sections is a PAGINATED envelope
          ({ crafts, pageNo, pageSize, totalElements, totalPages }) with pageSize
          hardcoded to 4 server-side. `totalElements` must be threaded through
          alongside the array — it's the only signal that more exist, and without
          it the rails' "See all" can never render. */}
      <CraftClusterRow
        title={t("myStore.shopClothingBy")}
        subtitle={t("myStore.clothingSubtitle")}
        items={home?.clothingCrafts?.crafts}
        total={home?.clothingCrafts?.totalElements}
        seeAllHref="/craft-cluster/1"
      />
      <CraftClusterRow
        title={t("myStore.shopJewelryBy")}
        subtitle={t("myStore.jewelrySubtitle")}
        items={home?.jewelryCrafts?.crafts}
        total={home?.jewelryCrafts?.totalElements}
        seeAllHref="/craft-cluster/2"
      />

      {/* NEW: philosophy + support-small-business trust bands */}
      <PhilosophyBand />
      <SupportSmallBiz />

      {/* education + behind the scenes (from get_home_page) */}
      <KnowYourCraftRow
        articles={home?.knowYourCrafts?.articles}
        total={home?.knowYourCrafts?.totalElements}
      />
      <BehindScenesRow
        videos={home?.behindTheScenes?.videos}
        total={home?.behindTheScenes?.totalElements}
      />

      {/* NEW: store footer (owner identity from about_me) */}
      <StoreFooter
        brandName={brandName}
        ownerName={ownerFirstName}
        ownerFullName={ownerFullName}
        storeLocation={storeLocation}
        contactAddress={
          storeLocation
            ? `${storeLocation}${about?.pinCode ? ` ${about.pinCode}` : ""}`
            : undefined
        }
        // Real store-owner GSTIN. about_me doesn't return this field yet, so it
        // stays hidden (no fabricated placeholder); shows once the backend adds it.
        gstin={about?.gstin || ""}
      />

      {/* NEW: WhatsApp FAB (Save-App button removed per design) */}
      <WhatsAppFAB ownerFirstName={ownerFirstName} onClick={onWhatsAppClick} />

      {/* Cookie consent — shown on the home page until the shopper chooses */}
    </div>
  );
}
